<template>
  <div class="staff-credits-sync-form">
    <franchise-select
      v-model="formData.franquia_id"
      elem-id="credits-sync-franquia-id"
      data-testid="credits-sync-franquia-id"
      :label="t('Franquia')"
    />
    <div v-if="errors.franquia_id" class="error-message" data-testid="credits-sync-error-franquia">{{ errors.franquia_id }}</div>
  </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { emitValidationFailed } from '../../../composables/useFormValidation';
import { eventBus } from '../../../eventBus';
import { useReconciliationSync } from '../../../composables/useReconciliationSync';
import { log } from '../../../utils/DebugLogger';
import type { Subscriptions_SubscriptionSyncRow } from '../../../contracts/Subscriptions_SubscriptionSyncRow';

const { t } = useI18n();
const { syncCreditPurchases } = useReconciliationSync();

/**
 * Result handed up to the modal on a successful sync — no `truncated`: the
 * credits door sweeps our own local ai_credit_purchases rows, never a
 * paginated remote listing, so that field never applies here (see the round
 * brief's "armadilha do truncated").
 */
export interface StaffCreditsSyncResult {
  franquiaId: number;
  rows: Subscriptions_SubscriptionSyncRow[];
}

const emit = defineEmits<{
  'submit-success': [result: StaffCreditsSyncResult];
}>();

const formData = reactive({
  franquia_id: null as number | string | null,
});

const errors = reactive({
  franquia_id: '',
});

function validateForm(): boolean {
  errors.franquia_id = '';

  let valid = true;

  if (!formData.franquia_id) {
    errors.franquia_id = t('Selecione uma franquia');
    valid = false;
  }

  if (!valid) {
    emitValidationFailed('StaffCreditsSyncForm', { ...errors });
  }

  return valid;
}

async function handleSubmit(): Promise<void> {
  log('StaffCreditsSyncForm', 'handleSubmit', 'Validating form');

  if (!validateForm()) {
    return;
  }

  const franquiaId = Number(formData.franquia_id);
  log('StaffCreditsSyncForm', 'handleSubmit', 'Submitting credit-purchase sync', { franquiaId });

  let rows: Subscriptions_SubscriptionSyncRow[];
  try {
    const result = await syncCreditPurchases(franquiaId);
    rows = result.rows;
  } catch (err) {
    // A recusa do backend chega em HTTP 200 (envelope success:false) e agora
    // rejeita no httpService (throwOnEnvelopeFailure). Sem este catch a
    // rejeição não teria dono: o modal ficaria aberto e mudo.
    log('StaffCreditsSyncForm', 'handleSubmit', 'Erro ao sincronizar compras de créditos', {
      message: (err as Error)?.message,
    });
    eventBus.emit('globalMessage.error', t('Erro ao sincronizar compras de créditos'));
    return;
  }

  formData.franquia_id = null;

  emit('submit-success', { franquiaId, rows });
}

defineExpose({
  handleSubmit,
  formData,
});
</script>

<style scoped>
.staff-credits-sync-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.error-message {
  color: var(--p-red-600, #dc2626);
  font-size: 0.875rem;
}
</style>
